/**
 * Product analytics (PostHog) public API (IC-132, ADR-6/7/8).
 *
 * Inert unless `config.analytics.enabled === true`: otherwise `posthog-js` is
 * never imported, and every function here is a no-op. Nothing in this module
 * throws or rejects, so analytics can never break the app. Callers import
 * from here only, never from `posthog-js`.
 */
import type { PostHog } from 'posthog-js';
import type { AnalyticsConfig } from '@/types';
import { sha256Prefix } from '@/lib/rum';
import type { AnalyticsEvents } from './events';
import { beforeSend } from './sanitize';

export type { AnalyticsEvents } from './events';
export { rowCountBucket, type RowBucket } from './events';

interface InitConfig {
  environment: string;
  version: string;
  analytics?: AnalyticsConfig;
}

let ph: PostHog | null = null;
let initStarted = false;
let appLoadedSent = false;
let superProps: Record<string, string> = {};

/*
 * Latest identity intent only (no event payloads, so this is not an event
 * queue and ADR-7's "no buffering" holds). The identity component can run on a
 * restored session before the lazily imported SDK is ready; replaying the
 * latest identity once after init avoids losing it.
 */
let pendingUserHash: string | null = null;
let pendingCustomer: { key: string; name?: string } | null = null;
let pendingSite: string | null = null;

function warn(what: string, err: unknown): void {
  if (import.meta.env.DEV) console.warn(`[analytics] ${what} failed`, err);
}

function safely(what: string, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    warn(what, err);
  }
}

function applyIdentity(client: PostHog): void {
  if (pendingUserHash) client.identify(pendingUserHash);
  if (pendingCustomer) {
    client.group('customer', pendingCustomer.key, pendingCustomer.name ? { name: pendingCustomer.name } : {});
  }
  if (pendingSite) client.register({ site_id: pendingSite });
}

export async function initAnalytics(config: InitConfig): Promise<void> {
  const analytics = config.analytics;
  if (!analytics || analytics.enabled !== true || !analytics.posthogKey) return;
  if (initStarted) return;
  initStarted = true;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(analytics.posthogKey, {
      api_host: analytics.apiHost,
      ui_host: analytics.uiHost,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      disable_session_recording: true,
      person_profiles: 'identified_only',
      before_send: beforeSend,
    });
    superProps = {
      app_version: config.version,
      environment: config.environment,
      customer_key: analytics.customerKey,
    };
    posthog.register(superProps);
    ph = posthog;
    safely('identity replay', () => applyIdentity(posthog));
    if (!appLoadedSent) {
      appLoadedSent = true;
      track('app_loaded', {});
    }
  } catch (err) {
    ph = null;
    warn('init', err);
  }
}

export function track<K extends keyof AnalyticsEvents>(event: K, props: AnalyticsEvents[K]): void {
  const client = ph;
  if (!client) return;
  safely(`capture ${event}`, () => {
    client.capture(event, props);
  });
}

export async function identifyAnalyticsUser(userId: string): Promise<void> {
  try {
    const hash = await sha256Prefix(userId);
    // 'anon' means no WebCrypto: identifying would merge every user into one person.
    if (hash === 'anon') return;
    pendingUserHash = hash;
    const client = ph;
    if (client) safely('identify', () => client.identify(hash));
  } catch (err) {
    warn('identify', err);
  }
}

export function setAnalyticsCustomer(key: string, name?: string): void {
  pendingCustomer = { key, name };
  const client = ph;
  if (client) safely('group', () => client.group('customer', key, name ? { name } : {}));
}

export function setAnalyticsSite(siteId: string | null): void {
  pendingSite = siteId;
  const client = ph;
  if (!client) return;
  safely('site', () => {
    if (siteId) client.register({ site_id: siteId });
    else client.unregister('site_id');
  });
}

export function resetAnalytics(): void {
  pendingUserHash = null;
  pendingCustomer = null;
  pendingSite = null;
  const client = ph;
  if (!client) return;
  safely('reset', () => {
    client.reset();
    // reset() also clears registered super-properties.
    client.register(superProps);
  });
}

/** Test-only: clears all module state so each test starts from a cold module. */
export function __resetAnalyticsForTests(): void {
  ph = null;
  initStarted = false;
  appLoadedSent = false;
  superProps = {};
  pendingUserHash = null;
  pendingCustomer = null;
  pendingSite = null;
}
